import { formatDbConfigConditions } from "../../../queries/regulator-portal/compliance/parseComplianceRule";
import type {
  ComplianceMonitoredDbConfig,
  ComplianceMonitoredWorkload,
  ComplianceRule,
  ComplianceRuleWritePayload,
} from "../../../queries/regulator-portal/compliance/types";

export type RuleFormWorkload = ComplianceMonitoredWorkload;

export type RuleFormGameStop = {
  name: string;
  resources: string;
};

export type RuleFormDbConfig = {
  tableName: string;
  region: string;
  fields: string;
  conditions: string;
};

export type RuleFormValues = {
  description: string;
  targetAccountId: string;
  baselineAccountId: string;
  monitoredWorkloads: RuleFormWorkload[];
  gameStops: RuleFormGameStop[];
  monitoredDbConfigs: RuleFormDbConfig[];
};

export const emptyWorkloadRow = (): RuleFormWorkload => ({
  resourceType: "",
  region: "",
  clusterName: "",
  namespace: "",
  deployment: "",
});

export const emptyGameStopRow = (): RuleFormGameStop => ({
  name: "",
  resources: "",
});

export const emptyDbConfigRow = (): RuleFormDbConfig => ({
  tableName: "",
  region: "",
  fields: "",
  conditions: "",
});

export function parseCommaSeparatedList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function parseConditionsInput(value: string): Record<string, string> {
  const conditions: Record<string, string> = {};
  for (const part of value.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const separator = trimmed.indexOf("=");
    if (separator <= 0) {
      continue;
    }
    conditions[trimmed.slice(0, separator).trim()] = trimmed
      .slice(separator + 1)
      .trim();
  }
  return conditions;
}

const joinList = (values: string[]): string => values.filter(Boolean).join(", ");

const trimWorkload = (row: RuleFormWorkload): ComplianceMonitoredWorkload => ({
  resourceType: row.resourceType?.trim() ?? "",
  region: row.region?.trim() ?? "",
  clusterName: row.clusterName?.trim() ?? "",
  namespace: row.namespace?.trim() ?? "",
  deployment: row.deployment?.trim() ?? "",
});

const isWorkloadPresent = (row: ComplianceMonitoredWorkload): boolean =>
  Object.values(row).some(Boolean);

const trimDbConfig = (row: RuleFormDbConfig): ComplianceMonitoredDbConfig => ({
  tableName: row.tableName?.trim() ?? "",
  region: row.region?.trim() ?? "",
  fields: parseCommaSeparatedList(row.fields ?? ""),
  conditions: parseConditionsInput(row.conditions ?? ""),
});

const isDbConfigPresent = (row: ComplianceMonitoredDbConfig): boolean =>
  Boolean(row.tableName) ||
  Boolean(row.region) ||
  row.fields.length > 0 ||
  Object.keys(row.conditions).length > 0;

export function gameStopsToMapping(
  rows: RuleFormGameStop[],
): Record<string, string[]> {
  const mapping: Record<string, string[]> = {};
  for (const row of rows) {
    const name = row.name?.trim() ?? "";
    if (!name) {
      continue;
    }
    mapping[name] = parseCommaSeparatedList(row.resources ?? "");
  }
  return mapping;
}

export function ruleToFormValues(rule: ComplianceRule): RuleFormValues {
  return {
    description: rule.description,
    targetAccountId: rule.targetAccountId,
    baselineAccountId: rule.baselineAccountId,
    monitoredWorkloads: rule.monitoredWorkloads.map((row) => ({ ...row })),
    gameStops: Object.entries(rule.indicatorMapping).map(([name, resources]) => ({
      name,
      resources: joinList(resources),
    })),
    monitoredDbConfigs: rule.monitoredDbConfigs.map((row) => ({
      tableName: row.tableName,
      region: row.region,
      fields: joinList(row.fields),
      conditions: formatDbConfigConditions(row.conditions),
    })),
  };
}

export type RuleWriteIdentity = Pick<
  ComplianceRule,
  "targetAccountId" | "baselineAccountId"
>;

export function formValuesToWritePayload(
  values: RuleFormValues,
  identity: RuleWriteIdentity,
): ComplianceRuleWritePayload {
  return {
    targetAccountId: identity.targetAccountId,
    baselineAccountId: identity.baselineAccountId,
    description: values.description?.trim() ?? "",
    monitoredWorkloads: (values.monitoredWorkloads ?? [])
      .map(trimWorkload)
      .filter(isWorkloadPresent),
    monitoredDbConfigs: (values.monitoredDbConfigs ?? [])
      .map(trimDbConfig)
      .filter(isDbConfigPresent),
    indicatorMapping: gameStopsToMapping(values.gameStops ?? []),
  };
}
